"use client"

/**
 * Tocador de áudio dos artigos do blog. Mesmo desenho e mesmas regras do tocador do Modo ON
 * (SDD-modo-on-tocador, brain): voltar e avançar 10 s, barra que se arrasta, velocidade em lista
 * (0,75x a 2x) lembrada no aparelho, "Você parou em ..." para continuar de onde parou, barra e
 * controles na tela bloqueada (MediaSession), leitor de tela com tempo falado e teclado.
 *
 * Posição e velocidade ficam só neste aparelho (localStorage), nunca vão para o servidor.
 * ARMADILHA herdada do Modo ON: o seek depende de Range. O servidor do .mp3 precisa responder 206.
 */

import { useCallback, useEffect, useRef, useState } from "react"

interface BlogAudioPlayerProps {
  src: string
  slug: string
  title: string
  durationSeconds?: number | null
  album?: string
  artwork?: string | null
}

const PULO = 10
const VELS: Array<[number, string]> = [[0.75, "mais devagar"], [1, "normal"], [1.25, ""], [1.5, ""], [2, "mais rápido"]]
const CH = "ebuz.blog.audio."

function le(k: string): string | null {
  try { return window.localStorage.getItem(k) } catch { return null }
}
function poe(k: string, v: string | null) {
  try { if (v === null) window.localStorage.removeItem(k); else window.localStorage.setItem(k, v) } catch { /* sem armazenamento */ }
}
function mmss(s: number): string {
  if (!isFinite(s) || s < 0) s = 0
  const m = Math.floor(s / 60)
  const r = Math.floor(s % 60)
  return `${m}:${String(r).padStart(2, "0")}`
}
function falado(s: number): string {
  s = Math.round(s || 0)
  const m = Math.floor(s / 60)
  const r = s % 60
  const p: string[] = []
  if (m) p.push(`${m} ${m === 1 ? "minuto" : "minutos"}`)
  if (r || !m) p.push(`${r} ${r === 1 ? "segundo" : "segundos"}`)
  return p.join(" e ")
}
function nomeVel(v: number): string {
  return `${String(v).replace(".", ",")}x`
}
function velGuardada(): number {
  const v = Number(le(CH + "vel"))
  return VELS.some(x => x[0] === v) ? v : 1
}

const Icone = {
  play: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5z" /></svg>,
  pausa: <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6.5" y="5" width="4" height="14" rx="1" /><rect x="13.5" y="5" width="4" height="14" rx="1" /></svg>,
  denovo: <svg className="traco" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>,
  gira: <svg viewBox="0 0 24 24" aria-hidden="true"><circle className="gira" cx="12" cy="12" r="9" strokeDasharray="40 20" /></svg>,
  volta: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>,
  avanca: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 4v5h-5" /></svg>,
}

export default function BlogAudioPlayer({ src, slug, title, durationSeconds, album, artwork }: BlogAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const salvoEm = useRef(0)
  const zerado = useRef(false)
  const abridor = useRef<HTMLButtonElement | null>(null)
  const [dur, setDur] = useState<number>(durationSeconds && durationSeconds > 0 ? durationSeconds : 0)
  const [atual, setAtual] = useState(0)
  const [tocando, setTocando] = useState(false)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState(false)
  const [fim, setFim] = useState(false)
  const [vel, setVel] = useState(1)
  const [retoma, setRetoma] = useState(0)
  const [arrasta, setArrasta] = useState<number | null>(null)
  const [folha, setFolha] = useState(false)

  const chavePos = `${CH}pos.${slug}`
  const duracao = useCallback((): number => {
    const a = audioRef.current
    if (a && isFinite(a.duration) && a.duration > 0) return a.duration
    return dur
  }, [dur])

  const salva = useCallback(() => {
    const a = audioRef.current
    if (!a || zerado.current) return
    const d = duracao()
    const t = a.currentTime || 0
    salvoEm.current = t
    if (d && t >= d - 5) poe(chavePos, null)
    else if (t > 5) poe(chavePos, t.toFixed(1))
  }, [chavePos, duracao])

  const posicaoBloqueio = useCallback(() => {
    const a = audioRef.current
    if (!a || typeof navigator === "undefined" || !("mediaSession" in navigator)) return
    try { navigator.mediaSession.playbackState = a.paused ? "paused" : "playing" } catch { /* navegador sem suporte */ }
    const d = duracao()
    if (!d || !navigator.mediaSession.setPositionState) return
    try { navigator.mediaSession.setPositionState({ duration: d, playbackRate: a.playbackRate || 1, position: Math.min(a.currentTime || 0, d) }) } catch { /* ignora */ }
  }, [duracao])

  const vaiPara = useCallback((v: number) => {
    const a = audioRef.current
    const d = duracao()
    if (!a || !d) return
    setRetoma(0)
    setFim(false)
    a.currentTime = Math.max(0, Math.min(d, v))
    setAtual(a.currentTime)
    posicaoBloqueio()
  }, [duracao, posicaoBloqueio])

  const toca = useCallback(() => {
    const a = audioRef.current
    if (!a) return
    zerado.current = false
    if (erro) { setErro(false); a.load() }
    const d = duracao()
    if (retoma) { a.currentTime = retoma; setRetoma(0) }
    else if (d && a.currentTime >= d - 0.25) a.currentTime = 0
    setCarregando(true)
    const p = a.play()
    if (p && p.catch) p.catch((e: { name?: string }) => { if (e && e.name === "AbortError") return; setCarregando(false) })
    ligaBloqueio()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erro, retoma, duracao])

  const pausa = useCallback(() => {
    const a = audioRef.current
    if (!a) return
    a.pause()
    setCarregando(false)
    salva()
  }, [salva])

  function ligaBloqueio() {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return
    const a = audioRef.current
    if (!a) return
    try {
      navigator.mediaSession.metadata = new MediaMetadata({
        title, artist: "eBuz", album: album || "Blog eBuz",
        artwork: artwork ? [{ src: artwork, sizes: "1200x630", type: "image/jpeg" }] : [],
      })
    } catch { /* ignora */ }
    const acao = (n: MediaSessionAction, fn: MediaSessionActionHandler | null) => { try { navigator.mediaSession.setActionHandler(n, fn) } catch { /* ignora */ } }
    acao("play", () => { void a.play() })
    acao("pause", () => { a.pause() })
    acao("seekbackward", () => { a.currentTime = Math.max(0, (a.currentTime || 0) - PULO) })
    acao("seekforward", () => { a.currentTime = Math.min(a.duration || 0, (a.currentTime || 0) + PULO) })
    acao("seekto", d => { if (d && typeof d.seekTime === "number" && isFinite(d.seekTime)) a.currentTime = d.seekTime })
  }

  // velocidade lembrada no aparelho
  useEffect(() => {
    const v = velGuardada()
    setVel(v)
    const a = audioRef.current
    if (a) {
      a.preservesPitch = true
      a.defaultPlaybackRate = v
      a.playbackRate = v
    }
  }, [])

  // guarda a posição ao sair da aba ou da página; solta os controles da tela bloqueada ao desmontar
  useEffect(() => {
    const aoEsconder = () => { if (document.visibilityState === "hidden") salva() }
    document.addEventListener("visibilitychange", aoEsconder)
    window.addEventListener("pagehide", salva)
    return () => {
      document.removeEventListener("visibilitychange", aoEsconder)
      window.removeEventListener("pagehide", salva)
      salva()
      if ("mediaSession" in navigator) {
        for (const n of ["play", "pause", "seekbackward", "seekforward", "seekto"] as MediaSessionAction[]) {
          try { navigator.mediaSession.setActionHandler(n, null) } catch { /* ignora */ }
        }
      }
    }
  }, [salva])

  // Escape fecha a folha de velocidade
  useEffect(() => {
    if (!folha) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); fechaFolha() } }
    document.addEventListener("keydown", onKey, true)
    return () => document.removeEventListener("keydown", onKey, true)
  }, [folha])

  function ofereceRetoma() {
    const a = audioRef.current
    if (!a) return
    const p = Number(le(chavePos))
    const d = duracao()
    const ok = p > 5 && (!d || p < d - 5) && a.paused && (a.currentTime || 0) < 1
    setRetoma(ok ? p : 0)
  }
  function soubeDuracao() {
    const a = audioRef.current
    if (!a) return
    if (isFinite(a.duration) && a.duration > 0) setDur(a.duration)
    ofereceRetoma()
    posicaoBloqueio()
  }
  function fechaFolha() {
    setFolha(false)
    if (abridor.current && abridor.current.isConnected) abridor.current.focus()
  }
  function escolheVel(v: number) {
    setVel(v)
    poe(CH + "vel", String(v))
    const a = audioRef.current
    if (a) { a.defaultPlaybackRate = v; a.playbackRate = v }
    posicaoBloqueio()
  }

  const d = duracao()
  const pos = arrasta !== null ? arrasta : atual
  const pct = d ? Math.min(100, (pos / d) * 100) : 0
  const rotuloPlay = carregando ? "Carregando" : tocando ? "Pausar" : fim ? "Ouvir de novo" : "Tocar"
  const iconePlay = carregando ? Icone.gira : tocando ? Icone.pausa : fim ? Icone.denovo : Icone.play

  return (
    <section className="bap" aria-label="Ouvir o artigo">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={soubeDuracao}
        onDurationChange={soubeDuracao}
        onPlaying={() => { setCarregando(false); setErro(false); setTocando(true); setFim(false); posicaoBloqueio() }}
        onWaiting={() => { if (audioRef.current && !audioRef.current.paused) setCarregando(true) }}
        onCanPlay={() => { if (audioRef.current?.paused) setCarregando(false) }}
        onPause={() => { setCarregando(false); setTocando(false); salva(); posicaoBloqueio() }}
        onEnded={() => { setCarregando(false); setTocando(false); setFim(true); poe(chavePos, null); posicaoBloqueio() }}
        onSeeked={() => posicaoBloqueio()}
        onRateChange={() => posicaoBloqueio()}
        onTimeUpdate={() => {
          const a = audioRef.current
          if (!a) return
          if (arrasta === null) setAtual(a.currentTime || 0)
          if (Math.abs((a.currentTime || 0) - salvoEm.current) >= 3) salva()
        }}
        onError={() => { setCarregando(false); setTocando(false); setErro(true) }}
      />
      <p className="bap-rot">
        Ouvir o artigo
        <small>{d ? ` · ${mmss(d)} · narração por voz sintética` : " · narração por voz sintética"}</small>
      </p>

      {retoma > 0 && (
        <div className="bap-retoma">
          <span>Você parou em {mmss(retoma)}.</span>
          <button type="button" className="bap-bt bap-bt-prim" onClick={toca}>Continuar</button>
          <button type="button" className="bap-bt" onClick={() => { poe(chavePos, null); setRetoma(0); const a = audioRef.current; if (a) a.currentTime = 0; toca() }}>Do início</button>
        </div>
      )}

      <input
        type="range"
        className="bap-seek"
        min={0}
        max={d || 0}
        step="any"
        value={Math.min(pos, d || 0)}
        disabled={!d}
        aria-label="Posição no áudio"
        aria-valuetext={d ? `${falado(pos)} de ${falado(d)}` : "carregando"}
        style={{ ["--p" as string]: `${pct}%` }}
        onPointerDown={() => setArrasta(atual)}
        onPointerUp={e => { const v = Number((e.target as HTMLInputElement).value); setArrasta(null); vaiPara(v) }}
        onPointerCancel={() => setArrasta(null)}
        onChange={e => { const v = Number(e.target.value); if (arrasta !== null) setArrasta(v); else vaiPara(v) }}
        onKeyDown={e => {
          const passo: Record<string, number> = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5, PageDown: -PULO, PageUp: PULO }
          const x = passo[e.key]
          if (x) { e.preventDefault(); vaiPara((audioRef.current?.currentTime || 0) + x) }
        }}
      />
      <div className="bap-tempos"><span>{mmss(pos)}</span><span>{d ? mmss(d) : "--:--"}</span></div>

      <div className="bap-ctrl">
        <button type="button" className="bap-pula" aria-label={`Voltar ${PULO} segundos`} onClick={() => vaiPara((audioRef.current?.currentTime || 0) - PULO)}>
          {Icone.volta}<b>{PULO}</b>
        </button>
        <button type="button" className="bap-play" aria-label={rotuloPlay} aria-busy={carregando} onClick={() => (tocando ? pausa() : toca())}>
          {iconePlay}
        </button>
        <button type="button" className="bap-pula" aria-label={`Avançar ${PULO} segundos`} onClick={() => vaiPara((audioRef.current?.currentTime || 0) + PULO)}>
          {Icone.avanca}<b>{PULO}</b>
        </button>
        <button type="button" ref={abridor} className="bap-vel" aria-haspopup="dialog" onClick={() => setFolha(true)}>
          Velocidade {nomeVel(vel)}
        </button>
      </div>

      {erro && (
        <p className="bap-erro" role="alert">
          Não consegui carregar o áudio.{" "}
          <button type="button" className="bap-bt" onClick={() => { setErro(false); audioRef.current?.load(); toca() }}>Tentar de novo</button>
        </p>
      )}

      {folha && (
        <div className="bap-raiz">
          <div className="bap-fundo" onClick={fechaFolha} />
          <div className="bap-folha" role="dialog" aria-modal="true" aria-labelledby="bap-folha-tit">
            <div className="bap-pega" aria-hidden="true" />
            <p className="bap-folha-tit" id="bap-folha-tit">Velocidade da voz</p>
            {VELS.map(([v, nome]) => (
              <label key={v} className="bap-op" htmlFor={`bap-v-${v}`}>
                <span>{nomeVel(v)}{nome ? <small>{nome}</small> : null}</span>
                <input type="radio" name="bap-vel" id={`bap-v-${v}`} value={v} checked={vel === v} autoFocus={vel === v} onChange={() => escolheVel(v)} />
              </label>
            ))}
            <button type="button" className="bap-ok" onClick={fechaFolha}>Pronto</button>
          </div>
        </div>
      )}
    </section>
  )
}

const CSS = `
.bap{background:var(--bg-alt,#f6f5f3);border:1px solid var(--border,#e6e4e0);border-radius:18px;padding:14px 16px 14px;margin:0 0 22px;max-width:640px;color:var(--fg1,#2b2f35)}
.bap-rot{font-weight:600;font-size:12px;letter-spacing:.06em;text-transform:uppercase;color:var(--fg2,#4a4f57);margin:0 0 4px}
.bap-rot small{font-weight:500;letter-spacing:0;text-transform:none;color:var(--fg3,#62676f);font-size:12px}
.bap-retoma{display:flex;align-items:center;gap:8px;flex-wrap:wrap;background:var(--bg-app,#fff);border:1px dashed var(--border-strong,#8a8f98);border-radius:12px;padding:8px 10px;margin:6px 0 4px;font-size:14px;color:var(--fg2,#4a4f57)}
.bap-retoma span{flex:1;min-width:130px}
.bap-bt{font-weight:600;font-size:12.5px;min-height:44px;border-radius:999px;padding:0 14px;border:1px solid var(--border-strong,#8a8f98);background:transparent;color:var(--fg1,#2b2f35);cursor:pointer}
.bap-bt-prim{background:var(--fg1,#2b2f35);color:var(--bg-app,#fff);border-color:var(--fg1,#2b2f35)}
.bap-seek{--p:0%;-webkit-appearance:none;appearance:none;display:block;width:100%;height:44px;margin:0;background:transparent;cursor:pointer;touch-action:pan-y}
.bap-seek::-webkit-slider-runnable-track{height:6px;border-radius:3px;background:linear-gradient(to right,var(--brand-primary,#ef3476) var(--p),var(--border,#e6e4e0) var(--p))}
.bap-seek::-moz-range-track{height:6px;border-radius:3px;background:var(--border,#e6e4e0)}
.bap-seek::-moz-range-progress{height:6px;border-radius:3px;background:var(--brand-primary,#ef3476)}
.bap-seek::-webkit-slider-thumb{-webkit-appearance:none;width:22px;height:22px;border-radius:50%;background:var(--fg1,#2b2f35);border:3px solid var(--bg-alt,#f6f5f3);margin-top:-8px}
.bap-seek::-moz-range-thumb{width:16px;height:16px;border-radius:50%;background:var(--fg1,#2b2f35);border:3px solid var(--bg-alt,#f6f5f3)}
.bap-seek:disabled{cursor:default;opacity:.55}
.bap-tempos{display:flex;justify-content:space-between;margin-top:-4px;font-size:12px;color:var(--fg3,#62676f);font-variant-numeric:tabular-nums}
.bap-ctrl{display:flex;align-items:center;justify-content:center;gap:22px;margin:10px 0 2px;flex-wrap:wrap}
.bap-pula{position:relative;width:52px;height:52px;border-radius:50%;border:1px solid var(--border,#e6e4e0);background:transparent;color:var(--fg1,#2b2f35);display:grid;place-items:center;cursor:pointer}
.bap-pula svg{width:24px;height:24px;fill:none;stroke:currentColor;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round}
.bap-pula b{position:absolute;font-weight:700;font-size:9.5px;top:53%;left:50%;transform:translate(-50%,-50%)}
.bap-play{width:64px;height:64px;border-radius:50%;border:0;background:var(--brand-primary,#ef3476);color:#fff;display:grid;place-items:center;cursor:pointer}
.bap-play svg{width:28px;height:28px;fill:currentColor}
.bap-play svg.traco,.bap-play .gira{fill:none;stroke:currentColor;stroke-linecap:round;stroke-linejoin:round}
.bap-play svg.traco{stroke-width:2.2}
.bap-play .gira{stroke-width:3;transform-origin:center;transform-box:fill-box;animation:bapGira 1s linear infinite}
@keyframes bapGira{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.bap-play .gira{animation:none}}
.bap-vel{min-height:44px;padding:0 16px;font-weight:600;font-size:13px;border:1px solid var(--border-strong,#8a8f98);border-radius:999px;background:transparent;color:var(--fg1,#2b2f35);cursor:pointer}
.bap-erro{margin:10px 0 0;font-size:14px;color:var(--fg2,#4a4f57);text-align:center}
.bap-pula:focus-visible,.bap-play:focus-visible,.bap-vel:focus-visible,.bap-bt:focus-visible,.bap-seek:focus-visible,.bap-ok:focus-visible,.bap-op:focus-within{outline:2px solid var(--brand-primary,#ef3476);outline-offset:2px}
.bap-fundo{position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,.4)}
.bap-folha{position:fixed;left:0;right:0;bottom:0;z-index:1001;max-width:560px;margin:0 auto;background:var(--bg-app,#fff);color:var(--fg1,#2b2f35);border-radius:18px 18px 0 0;padding:12px 18px 18px;box-shadow:0 -8px 30px rgba(0,0,0,.25)}
.bap-pega{width:38px;height:4px;border-radius:2px;background:var(--border-strong,#8a8f98);margin:0 auto 12px}
.bap-folha-tit{font-weight:600;font-size:17px;margin:0 0 6px;font-family:var(--font-display,inherit)}
.bap-op{display:flex;align-items:center;justify-content:space-between;min-height:48px;padding:8px 4px;border-bottom:1px solid var(--border,#e6e4e0);font-size:15px;cursor:pointer}
.bap-op small{color:var(--fg3,#62676f);font-size:12.5px;margin-left:6px}
.bap-op input{width:22px;height:22px;accent-color:var(--brand-primary,#ef3476);margin:0}
.bap-ok{display:block;width:100%;margin-top:14px;min-height:48px;border:0;border-radius:12px;background:var(--fg1,#2b2f35);color:var(--bg-app,#fff);font-weight:600;font-size:14px;cursor:pointer}
`
